import { useEffect, useState,useRef } from 'react';
import {
  Box,
  Button,
  alpha,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Grid,
} from '@mui/material';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppSearchBar from '@projectK/components/AppSearchBar';
import AppsPagination from '@projectK/components/AppsPagination';
import jwtAxios from '@projectK/services/auth/JWT';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import { useIntl } from 'react-intl';
import useMediaQuery from '@mui/material/useMediaQuery';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AppsHeader from '@projectK/components/AppsHeader';
import AppsContainer from '@projectK/components/AppsContainer';
import AppsContent from '@projectK/components/AppsContent';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import FilterListIcon from '@mui/icons-material/FilterList';
import { mobileExportCsv } from '@projectK/ReUseFunctions';
import { useRolePermissions,hasPermission } from '@projectK/context/RolesProvider';
import FeedbackFilter from './FeedbackFilters';
import {
  FeedbackReportsMain,
  SortingModalFeedbackMain,
} from '@projectK/MainComponents';
import { UnfoldMoreOutlined } from '@mui/icons-material';

const FeedbackReportTable = () => {
  const companyTheme = CompanyTheme();
  const { rolePermissions } = useRolePermissions();
  const { isMobile } = useMobileSettings();
  const isMobileResponse = useMediaQuery('(max-width:755px)');
  const { user } = useAuthUser();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { messages } = useIntl();
  const [yearFilter, setYearFilter] = useState(null);
  const [activityFilter, setActivityFilter] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [productNameSorting, setProductNameSorting] = useState('');
  const [feedbackSorting, setFeedbackSorting] = useState('');
  const [startDateSorting, setStartDateSorting] = useState(true);
  const [activityNameSorting, setActivityNameSorting] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [timeFilter, setTimeFilter] = useState('');
  const [perPage, setPerPage] = useState(20);
  const [openSortingModal, setOpenSortingModal] = useState(false);
  const currentYear = new Date().getFullYear();
  const abortControllerRef = useRef(null);
  // Generate an array of years up to the current year
  const years = Array.from(
    { length: currentYear - 2020 + 1 },
    (_, i) => currentYear - i,
  );
  const fetchFeedbackData = async () => {
    try {
      setLoading(true);
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;
      const response = await jwtAxios.get(
        `/user-feedback/all_feedback/${user?.id}`,
        {
          params: {
            search: search,
            page: page,
            perPage: perPage,
            filters: {
              productId: productFilter,
              year: yearFilter,
              activityId: activityFilter,
              startDate: dateFilter,
              startTime: timeFilter,
            },
            sortings: {
              product_name: productNameSorting,
              activity_name: activityNameSorting,
              feedback: feedbackSorting,
              start_date: startDateSorting,
            },
          },
          signal,
        },
      );
      const { data } = response;
      setList(data.list);
      setTotal(data.total);
      data.length === 0 && page > 0 && setPage(page - 1);
      setLoading(false);
    } catch (error) {
      if (error.message !== 'canceled') {
         console.error(error);
          setLoading(false);
      }  
    }
  };
  useEffect(() => {
    fetchFeedbackData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    search,
    page,
    perPage,
    productFilter,
    yearFilter,
    activityFilter,
    productNameSorting,
    feedbackSorting,
    activityNameSorting,
    startDateSorting,
    dateFilter,
    timeFilter,
  ]);
  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const response = await jwtAxios.get(
        `/user-feedback/all_feedback/export/data/${user?.id}`,
        {
          params: {
            search: search,
            page: page,
            perPage: 'all',
            filters: {
              productId: productFilter,
              year: yearFilter,
              activityId: activityFilter,
              startDate: dateFilter,
              startTime: timeFilter,
            },
            sortings: {
              product_name: productNameSorting,
              activity_name: activityNameSorting,
              feedback: feedbackSorting,
              start_date: startDateSorting,
            },
          },
          responseType: 'arrayBuffer',
        },
      );
      // Create a Blob from the response data
      const blob = new Blob([response.data], {
        type: `application/csv`,
      });
      if (isMobile) {
        await mobileExportCsv(blob, 'feedback', 'csv');
      } else {
        // Create a link element to trigger the download
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `feedback.csv`;
        link.click();
      }
    } catch (error) {
      console.error(error.response.data.error);
      // Handle error as needed
    } finally {
      setIsExporting(false);
    }
  };
  const updatedData = list?.map((data) => ({
    ...data,
    activity_name: data.activity_display_name
      ? data.activity_display_name
      : data.activity_name,
  }));

  const onPageChange = (event, value) => {
    setPage(value);
  };
  const onSearchOrder = (value) => {
    setSearch(value);
    setPage(0);
  };

  const handleSort = (column) => {
    setFeedbackSorting(column === 'feedback' ? !feedbackSorting : null);
    setActivityNameSorting(
      column === 'activity_name' ? !activityNameSorting : null,
    );

    setProductNameSorting(
      column === 'product_name' ? !productNameSorting : null,
    );
    setStartDateSorting(column === 'start_date' ? !startDateSorting : null);
  };
  const handleChangePerPage = (event) => {
    const newPerPage = event.target.value;
    setPerPage(newPerPage);
    setPage(0);
  };
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const handleFilterModalOpen = () => {
    setOpenFilterModal(true);
  };
  const handleFilterModalClose = () => {
    setOpenFilterModal(false);
  };
  const handleClearFilter = () => {
    setProductNameSorting(null);
    setActivityNameSorting(null);
    setStartDateSorting(null);
    setFeedbackSorting(null);
  };
  return (
    <div className='event-search ps-h'>
      <FeedbackFilter
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        productFilter={productFilter}
        setProductFilter={setProductFilter}
        activityFilter={activityFilter}
        setActivityFilter={setActivityFilter}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        timeFilter={timeFilter}
        setTimeFilter={setTimeFilter}
        setPage={setPage}
        yearFilter={yearFilter}
        setYearFilter={setYearFilter}
        years={years}
        isMobile={isMobile}
        openFilterModal={openFilterModal}
        handleFilterModalClose={handleFilterModalClose}
      />
      {isMobile && (
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: 1,
            justifyContent: 'space-between',
          }}
        >
          <Box onClick={handleFilterModalOpen} className='mt-15'>
            <span>Filters</span>
            <IconButton
              size='small'
              sx={{ fontWeight: 'bold' }}
              className='icon-btn'
            >
              <FilterListIcon />
            </IconButton>
          </Box>
          <Box onClick={() => setOpenSortingModal(true)} className='mt-15'>
            <span>Sort</span>
            <IconButton
              size='small'
              sx={{ fontWeight: 'bold' }}
              className='icon-btn'
            >
              <UnfoldMoreOutlined />
            </IconButton>
          </Box>
        </Grid>
      )}
      <AppsContainer fullView>
        <AppsHeader>
          <Box
            display='flex'
            flexDirection='row'
            alignItems='center'
            width={1}
            justifyContent='space-between'
            className='main-box'
          >
            <Box display='flex' flexDirection='row' className='search-box'>
              <AppSearchBar
                iconPosition='right'
                overlap={false}
                onChange={(event) => onSearchOrder(event.target.value)}
                placeholder={messages['common.searchHere']}
              />
            </Box>
            <Box
              display='flex'
              flexDirection='row'
              alignItems='center'
              className='button-boxes'
            >
              {hasPermission(rolePermissions, 'Player Reports - Feedback Report', 'export') && (
              <Button
                variant='outlined'
                size='small'
                onClick={handleExportCSV}
                sx={{
                  position: 'relative',
                  ...(isMobileResponse ? { ml: 2.5 } : { ml: 1.5 }),
                  ...(isMobileResponse ? { minWidth: 0 } : { minWidth: 160 }),
                  borderColor: companyTheme
                    ? companyTheme.primary.main
                    : '#2196f3',
                  '&:hover': {
                    borderColor: companyTheme
                      ? alpha(companyTheme.primary.main, 0.8)
                      : alpha('#2196f3', 0.8),
                  },
                  color: companyTheme ? companyTheme.primary.main : '#2196f3',
                  textTransform: 'none',
                }}
                disabled={isExporting}
              >
                {isMobileResponse ? (
                  <FileDownloadOutlinedIcon sx={{ mr: 1 }} />
                ) : (
                  <>
                    <FileDownloadOutlinedIcon sx={{ mr: 1 }} />
                    Export to CSV
                  </>
                )}
              </Button>
              )}
             <Box sx={{ display: { xs: "none", sm: "flex" ,alignItems:'center'} } } className="fb-pagination">
                <FormControl
                  variant='standard'
                  sx={{
                    borderRadius: 50,
                  }}
                  size='small'

                >
                  <Select
                    value={perPage}
                    onChange={handleChangePerPage}
                    sx={{ marginLeft: '10px' }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={30}>30</MenuItem>
                    <MenuItem value={40}>40</MenuItem>
                    <MenuItem value={60}>60</MenuItem>
                    <MenuItem value={80}>80</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <AppsPagination
                  rowsPerPage={perPage}
                  count={total}
                  page={page}
                  onPageChange={onPageChange}
                />
              </Box>
            </Box>
          </Box>
        </AppsHeader>
        <AppsContent
          className='evnt-s-table ps-events'
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
          }}
        >
          {openSortingModal && (
            <SortingModalFeedbackMain
              isOpen={openSortingModal}
              onClose={() => setOpenSortingModal(false)}
              clearFilter={handleClearFilter}
              sortings={{
                productNameSorting,
                setProductNameSorting,
                activityNameSorting,
                setActivityNameSorting,
                startDateSorting,
                setStartDateSorting,
                feedbackSorting,
                setFeedbackSorting,
              }}
              companyTheme={companyTheme}
            />
          )}

          <FeedbackReportsMain
            companyTheme={companyTheme}
            productNameSorting={productNameSorting}
            activityNameSorting={activityNameSorting}
            startDateSorting={startDateSorting}
            feedbackSorting={feedbackSorting}
            updatedData={updatedData}
            loading={loading}
            handleSort={handleSort}
            isExporting={isExporting}
          />
          <Box sx={{ display: { xs: "block", sm: "none" } }} className="fb-bottompagination">
            <FormControl
              variant='standard'
              sx={{
                borderRadius: 50,
              }}
              size='small'
              className='cols-count colc'
            >
              <Select
                value={perPage}
                onChange={handleChangePerPage}
                sx={{ marginLeft: '10px', height: '34px' }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={60}>60</MenuItem>
                <MenuItem value={80}>80</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            <AppsPagination
              className='cols-pagination'
              rowsPerPage={perPage}
              count={total}
              page={page}
              onPageChange={onPageChange}
            />
          </Box>
        </AppsContent>
      </AppsContainer>
    </div>
  );
};

export default FeedbackReportTable;
